/**
 * Real boot warm-up signals — drives the slim status strip (no fake progress).
 * State names only; strip hides when all required signals reach steady state.
 */

export type BootWarmupPhase =
  | 'auth'
  | 'daemon_ipc'
  | 'baton'
  | 'covers'
  | null;

export type BootWarmupSnapshot = {
  authReady: boolean;
  authedApiReady: boolean;
  daemonIpcReady: boolean;
  batonReady: boolean;
  coversIdle: boolean;
  dismissed: boolean;
  phase: BootWarmupPhase;
  message: string | null;
  visible: boolean;
};

type Listener = () => void;

let _authReady = false;
let _authedApiReady = false;
let _daemonIpcReady = true;
let _batonReady = true;
let _coversIdle = true;
let _dismissed = false;
let _requireDaemonIpc = false;
let _requireBaton = false;
let _listeners = new Set<Listener>();

function emit(): void {
  _listeners.forEach((fn) => {
    try { fn(); } catch { /* ignore */ }
  });
}

export function subscribeBootWarmupSignals(listener: Listener): () => void {
  _listeners.add(listener);
  return () => { _listeners.delete(listener); };
}

export function resetBootWarmupSignalsForTests(): void {
  _authReady = false;
  _authedApiReady = false;
  _daemonIpcReady = true;
  _batonReady = true;
  _coversIdle = true;
  _dismissed = false;
  _requireDaemonIpc = false;
  _requireBaton = false;
  _listeners.clear();
}

export function configureBootWarmupRequirements(opts: {
  requireDaemonIpc?: boolean;
  requireBaton?: boolean;
}): void {
  _requireDaemonIpc = !!opts.requireDaemonIpc;
  _requireBaton = !!opts.requireBaton;
  if (!_requireDaemonIpc) _daemonIpcReady = true;
  if (!_requireBaton) _batonReady = true;
  emit();
}

export function noteBootAuthReady(ready: boolean): void {
  if (_authReady === ready) return;
  _authReady = ready;
  if (!ready) _authedApiReady = false;
  emit();
}

export function noteBootAuthedApiReady(ready: boolean): void {
  if (_authedApiReady === ready) return;
  _authedApiReady = ready;
  emit();
}

export function noteBootDaemonIpcReady(ready: boolean): void {
  if (_daemonIpcReady === ready) return;
  _daemonIpcReady = ready;
  emit();
}

export function noteBootBatonReady(ready: boolean): void {
  if (_batonReady === ready) return;
  _batonReady = ready;
  emit();
}

export function noteBootCoverWarmupActive(active: boolean): void {
  const idle = !active;
  if (_coversIdle === idle) return;
  _coversIdle = idle;
  emit();
}

export function dismissBootWarmupStrip(): void {
  if (_dismissed) return;
  _dismissed = true;
  emit();
}

export function isBootWarmupSteadyState(): boolean {
  if (_dismissed) return true;
  if (!_authReady || !_authedApiReady) return false;
  if (_requireDaemonIpc && !_daemonIpcReady) return false;
  if (_requireBaton && !_batonReady) return false;
  if (!_coversIdle) return false;
  return true;
}

export function resolveBootWarmupPhase(): BootWarmupPhase {
  if (isBootWarmupSteadyState()) return null;
  if (!_authReady || !_authedApiReady) return 'auth';
  if (_requireDaemonIpc && !_daemonIpcReady) return 'daemon_ipc';
  if (_requireBaton && !_batonReady) return 'baton';
  if (!_coversIdle) return 'covers';
  return null;
}

export function resolveBootWarmupMessage(phase: BootWarmupPhase): string | null {
  switch (phase) {
    case 'auth':
      return 'Finishing sign-in…';
    case 'daemon_ipc':
      return 'Connecting to your library…';
    case 'baton':
      return 'Connecting to Maestro…';
    case 'covers':
      return 'Warming up covers…';
    default:
      return null;
  }
}

export function getBootWarmupSnapshot(): BootWarmupSnapshot {
  const phase = resolveBootWarmupPhase();
  const steady = phase === null;
  return {
    authReady: _authReady,
    authedApiReady: _authedApiReady,
    daemonIpcReady: _daemonIpcReady,
    batonReady: _batonReady,
    coversIdle: _coversIdle,
    dismissed: _dismissed,
    phase,
    message: steady ? null : resolveBootWarmupMessage(phase),
    visible: !steady && !_dismissed,
  };
}
